import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Plus, X, Trash2, Building2, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { STATUS_MAP } from './dashboardUtils';
import ServiceCatalogPicker from './ServiceCatalogPicker';
import AIEstimationCard from './AIEstimationCard';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const INITIAL_FORM = {
  customerId: '', customerName: '', carPlate: '', vehicleId: '', carDetails: '',
  services: [], selectedServiceIds: [], serviceNotes: '',
  serviceDetails: [], totalPrice: 0, paidAmount: 0,
  notes: '', customerNotes: '', arrivalDate: '', targetCompletionDate: '',
  salesId: '', salesName: '', technicianId: '', technicianName: '',
  branchId: '', installationDate: '', warrantyYears: '', bonusPercentage: 0,
  status: '',
};

const AddEditJob = ({
  isOpen, onClose, job, customers,
  staffList: staff,
  vehicles: allVehicles,
  selectedBranchId, onSave, branches,
  userPermissions,
  customerId: defaultCustomerId,
}) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCatalog, setShowCatalog] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [estLoading, setEstLoading] = useState(false);
  const [showEstDetail, setShowEstDetail] = useState(false);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [showNewVehicleModal, setShowNewVehicleModal] = useState(false);
  const [newVehicleForm, setNewVehicleForm] = useState({ plate: '', brand: '', model: '' });

  // Guard: prevents re-initialization while modal is open and user is editing
  const initRef = useRef(null);

  const activeStaff = useMemo(() => (staff || []).filter(s => !s.isDeleted), [staff]);

  const salesStaff = useMemo(() =>
    activeStaff.filter(s => ['admin', 'manager', 'sales'].includes(s.role)),
    [activeStaff]
  );
  const techStaff = useMemo(() =>
    activeStaff.filter(s => ['admin', 'manager', 'technician'].includes(s.role)),
    [activeStaff]
  );

  const branchFilteredSales = useMemo(() => {
    const bid = formData.branchId;
    if (!bid || bid === 'all') return salesStaff;
    return salesStaff.filter(s =>
      !s.primaryBranchId || s.primaryBranchId === bid ||
      (s.accessibleBranchIds || []).includes(bid)
    );
  }, [salesStaff, formData.branchId]);

  const branchFilteredTech = useMemo(() => {
    const bid = formData.branchId;
    if (!bid || bid === 'all') return techStaff;
    return techStaff.filter(s =>
      !s.primaryBranchId || s.primaryBranchId === bid ||
      (s.accessibleBranchIds || []).includes(bid)
    );
  }, [techStaff, formData.branchId]);

  const token = getFromLocalStorage('accessToken');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  // Load catalog on open
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        const [catRes, svcRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/service-catalog/categories`, { headers }),
          fetch(`${BACKEND_URL}/api/service-catalog/services`, { headers }),
        ]);
        if (catRes.ok) { const d = await catRes.json(); setCategories(d.categories || []); }
        if (svcRes.ok) { const d = await svcRes.json(); setCatalog(d.services || []); }
      } catch (e) { console.error('Load catalog error:', e); }
    };
    load();
  }, [isOpen]);

  // Hydrate form ONLY on open — not on polling-triggered prop changes
  // initRef prevents re-init while modal stays open
  useEffect(() => {
    if (!isOpen) {
      initRef.current = null;
      return;
    }
    // Build unique key for this session
    const key = job ? `edit-${job.id}` : `create-${defaultCustomerId || 'none'}`;
    if (initRef.current === key) return; // already initialized for this session
    initRef.current = key;

    if (job) {
      setFormData({
        customerId: job.customerId || '', customerName: job.customerName || '',
        carPlate: job.carPlate || '', vehicleId: job.vehicleId || '', carDetails: job.carDetails || '',
        services: job.services || [], selectedServiceIds: job.selectedServiceIds || [],
        serviceNotes: job.serviceNotes || '',
        serviceDetails: (job.serviceDetails || []).map(sd => ({
          name: sd.name || '', brand: sd.brand || '', product: sd.product || '',
          material: sd.material || '', installationDate: sd.installationDate || '',
          warrantyYears: sd.warrantyYears ?? '', notes: sd.notes || '',
        })),
        totalPrice: job.totalPrice || 0, paidAmount: job.paidAmount || 0,
        notes: job.notes || '', customerNotes: job.customerNotes || '',
        arrivalDate: job.arrivalDate || '', targetCompletionDate: job.targetCompletionDate || '',
        salesId: job.salesId || '', salesName: job.salesName || '',
        technicianId: job.technicianId || '', technicianName: job.technicianName || '',
        branchId: job.branchId || '', installationDate: job.installationDate || '',
        warrantyYears: job.warrantyYears ?? '', bonusPercentage: job.bonusPercentage || 0,
        status: job.status || '',
      });
    } else {
      // CREATE mode — read defaults from props at init time only
      const init = {
        ...INITIAL_FORM,
        arrivalDate: new Date().toISOString().split('T')[0],
        branchId: (selectedBranchId && selectedBranchId !== 'all') ? selectedBranchId : '',
      };
      if (defaultCustomerId) {
        const c = (customers || []).find(cu => cu.id === defaultCustomerId);
        if (c) {
          init.customerId = defaultCustomerId;
          init.customerName = c.name || '';
          init.carPlate = c.carPlate || '';
        }
      }
      setFormData(init);
    }
    setEstimation(null); setShowCatalog(false);
    // Hydrate customer vehicles
    const cid = job ? (job.customerId || '') : (defaultCustomerId || '');
    if (cid) {
      const cVehicles = (allVehicles || []).filter(v => v.customerId === cid && !v.isDeleted);
      setCustomerVehicles(cVehicles);
    } else {
      setCustomerVehicles([]);
    }
  }, [isOpen, job, defaultCustomerId]);

  const filteredCatalog = useMemo(() => {
    let list = catalog;
    if (selectedCategory && selectedCategory !== 'all') list = list.filter(s => s.categoryId === selectedCategory);
    if (searchTerm) { const term = searchTerm.toLowerCase(); list = list.filter(s => s.name.toLowerCase().includes(term) || (s.code || '').toLowerCase().includes(term)); }
    return list;
  }, [catalog, selectedCategory, searchTerm]);

  const selectedServices = useMemo(() => catalog.filter(s => formData.selectedServiceIds.includes(s.id)), [catalog, formData.selectedServiceIds]);

  const addService = useCallback((svc) => {
    if (formData.selectedServiceIds.includes(svc.id)) return;
    setFormData(prev => ({
      ...prev,
      selectedServiceIds: [...prev.selectedServiceIds, svc.id],
      services: [...prev.services, svc.name],
      serviceDetails: [...prev.serviceDetails, { name: svc.name, brand: '', product: '', material: '', installationDate: '', warrantyYears: '', notes: '' }],
    }));
    setShowCatalog(false); setSearchTerm('');
  }, [formData.selectedServiceIds]);

  const removeService = useCallback((svcId) => {
    const svc = catalog.find(s => s.id === svcId);
    setFormData(prev => ({
      ...prev,
      selectedServiceIds: prev.selectedServiceIds.filter(id => id !== svcId),
      services: svc ? prev.services.filter(s => s !== svc.name) : prev.services,
      serviceDetails: svc ? prev.serviceDetails.filter(sd => sd.name !== svc.name) : prev.serviceDetails,
    }));
  }, [catalog]);

  const updateServiceDetail = useCallback((idx, field, value) => {
    setFormData(prev => {
      const details = [...prev.serviceDetails];
      if (details[idx]) details[idx] = { ...details[idx], [field]: value };
      return { ...prev, serviceDetails: details };
    });
  }, []);

  const handleCustomerChange = (customerId) => {
    const c = (customers || []).find(cu => cu.id === customerId);
    if (c) {
      // Client-side filter from allVehicles prop
      const cVehicles = (allVehicles || []).filter(v => v.customerId === customerId && !v.isDeleted);
      setCustomerVehicles(cVehicles);
      const firstV = cVehicles[0];
      setFormData(prev => ({
        ...prev, customerId, customerName: c.name,
        carPlate: firstV?.plate || c.carPlate || '',
        vehicleId: firstV?.id || '',
        carDetails: firstV ? `${firstV.brand || ''} ${firstV.model || ''}`.trim() : (c.carDetails || ''),
      }));
    } else {
      setCustomerVehicles([]);
    }
  };

  const handleStaffChange = (field, staffId) => {
    const s = (staff || []).find(st => st.id === staffId);
    const nameField = field === 'salesId' ? 'salesName' : 'technicianName';
    setFormData(prev => ({ ...prev, [field]: staffId, [nameField]: s?.name || '' }));
  };

  const fetchEstimation = async () => {
    if (formData.selectedServiceIds.length === 0) { toast({ title: "Uyari", description: "Oneri icin en az bir hizmet secin", variant: "destructive" }); return; }
    setEstLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/estimation/estimate`, {
        method: 'POST', headers,
        body: JSON.stringify({ selectedServiceIds: formData.selectedServiceIds, vehicleBrand: (formData.carDetails || '').split(' ')[0] || '', branchId: formData.branchId || selectedBranchId }),
      });
      if (res.ok) { const d = await res.json(); setEstimation(d); setShowEstDetail(true); }
    } catch (e) { console.error('Estimation error:', e); }
    setEstLoading(false);
  };

  const applyEstimation = (field) => {
    if (!estimation) return;
    if ((field === 'price' || field === 'all') && estimation.price) {
      setFormData(prev => ({ ...prev, totalPrice: estimation.price.suggestedPrice }));
      if (field === 'price') toast({ title: "Fiyat uygulandi", description: `${estimation.price.suggestedPrice} TL` });
    }
    if ((field === 'duration' || field === 'all') && estimation.duration) {
      const days = Math.ceil(estimation.duration.suggestedDuration / 24);
      const target = new Date(); target.setDate(target.getDate() + days);
      setFormData(prev => ({ ...prev, targetCompletionDate: target.toISOString().split('T')[0] }));
      if (field === 'duration') toast({ title: "Sure uygulandi", description: `${estimation.duration.suggestedDuration} saat (~${days} gun)` });
    }
    if (field === 'all') toast({ title: "Oneri uygulandi" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) { toast({ title: "Hata", description: "Musteri secimi zorunlu", variant: "destructive" }); return; }
    if (formData.selectedServiceIds.length === 0 && formData.services.length === 0) { toast({ title: "Hata", description: "En az bir hizmet secin", variant: "destructive" }); return; }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        totalPrice: parseFloat(formData.totalPrice) || 0,
        currentPrice: parseFloat(formData.totalPrice) || 0,
        paidAmount: parseFloat(formData.paidAmount) || 0,
        bonusPercentage: parseInt(formData.bonusPercentage) || 0,
        warrantyYears: formData.warrantyYears !== '' ? parseInt(formData.warrantyYears) : null,
        installationDate: formData.installationDate || null,
        branchId: formData.branchId || selectedBranchId || '',
        status: formData.status || undefined,
        serviceDetails: formData.serviceDetails.map(sd => ({
          ...sd,
          warrantyYears: sd.warrantyYears !== '' ? parseInt(sd.warrantyYears) || null : null,
        })),
      };
      const url = job ? `${BACKEND_URL}/api/jobs/${job.id}` : `${BACKEND_URL}/api/jobs`;
      const method = job ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `${method} failed`); }
      toast({ title: job ? "Is guncellendi" : "Is olusturuldu" });
      if (onSave) onSave();
      onClose();
    } catch (err) { toast({ title: "Hata", description: err.message, variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const f = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const activeBranches = (branches || []).filter(b => !b.isDeleted);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onClose(); }}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-display tracking-tight">{job ? 'Is Duzenle' : 'Yeni Is Olustur'}</DialogTitle>
          <DialogDescription className="text-gray-400">Musteri, hizmet ve fiyat bilgilerini girin</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ───── MUSTERI & ARAC ───── */}
          <div className="border border-blue-700/40 rounded-lg p-3 space-y-3 bg-blue-900/10">
            <span className="text-blue-400 font-semibold font-display text-xs">Musteri & Arac</span>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Musteri *</Label>
                <select value={formData.customerId} onChange={(e) => handleCustomerChange(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm" data-testid="job-customer-select">
                  <option value="">Musteri sec...</option>
                  {(customers || []).map(c => <option key={c.id} value={c.id}>{c.name} {c.carPlate ? `(${c.carPlate})` : ''}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Araç</Label>
                <div className="flex gap-2">
                  <select value={formData.vehicleId || ''} onChange={(e) => {
                    const v = customerVehicles.find(x => x.id === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      vehicleId: e.target.value,
                      carPlate: v?.plate || '',
                      carDetails: v ? `${v.brand || ''} ${v.model || ''}`.trim() : '',
                    }));
                  }}
                    className="flex-1 bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm font-mono" data-testid="job-vehicle-select">
                    <option value="">Araç seç...</option>
                    {customerVehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.plate} — {v.brand || ''} {v.model || ''}</option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setShowNewVehicleModal(true)}
                    className="shrink-0 border-gray-600 text-gray-300 hover:bg-gray-700 text-xs h-auto"
                    data-testid="add-vehicle-btn">
                    <Plus className="w-3 h-3 mr-1" />Yeni Araç
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Plaka</Label>
                <Input value={formData.carPlate} onChange={f('carPlate')}
                  className="bg-gray-900 border-gray-700 text-white text-sm font-mono tracking-wider" data-testid="job-plate-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Arac Detaylari (Marka / Model)</Label>
                <Input value={formData.carDetails} onChange={f('carDetails')} placeholder="Or: BMW 3 Serisi, Mercedes C200..."
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-car-details" />
              </div>
            </div>
          </div>

          {/* ───── SUBE ───── */}
          {activeBranches.length > 0 && (
            <div className="border border-indigo-700/40 rounded-lg p-3 space-y-2 bg-indigo-900/10">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-400" />
                <span className="text-indigo-400 font-semibold font-display text-xs">Sube</span>
              </div>
              <select value={formData.branchId} onChange={f('branchId')}
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm"
                data-testid="job-branch-select">
                <option value="">Sube sec...</option>
                {activeBranches.map(b => (
                  <option key={b.id} value={b.id}>{b.name} {b.code ? `(${b.code})` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* ───── HIZMETLER ───── */}
          <ServiceCatalogPicker
            showCatalog={showCatalog} setShowCatalog={setShowCatalog}
            selectedServices={selectedServices} removeService={removeService}
            filteredCatalog={filteredCatalog} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
            categories={categories} addService={addService} job={job}
            formData={{ ...formData, onServiceNotesChange: (v) => setFormData(prev => ({ ...prev, serviceNotes: v })) }}
          />

          {/* ───── HIZMET DETAYLARI (Marka/Malzeme/Garanti) ───── */}
          {formData.serviceDetails.length > 0 && (
            <div className="border border-purple-700/40 rounded-lg p-3 space-y-3 bg-purple-900/10">
              <span className="text-purple-400 font-semibold font-display text-xs">Hizmet Detaylari (Marka / Malzeme / Garanti)</span>
              {formData.serviceDetails.map((sd, idx) => (
                <div key={`sd-${sd.name}-${idx}`} className="bg-gray-900/50 border border-gray-700/40 rounded-lg p-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs font-medium">{sd.name}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-gray-500 text-[10px]">Urun Markasi</Label>
                      <Input value={sd.brand} onChange={(e) => updateServiceDetail(idx, 'brand', e.target.value)}
                        placeholder="Or: 3M, Llumar..." className="bg-gray-800 border-gray-700 text-white text-xs h-7"
                        data-testid={`sd-brand-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-[10px]">Malzeme / Urun</Label>
                      <Input value={sd.material} onChange={(e) => updateServiceDetail(idx, 'material', e.target.value)}
                        placeholder="Or: Satin, Gloss..." className="bg-gray-800 border-gray-700 text-white text-xs h-7"
                        data-testid={`sd-material-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-[10px]">Uygulama Tarihi</Label>
                      <Input type="date" value={sd.installationDate} onChange={(e) => updateServiceDetail(idx, 'installationDate', e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white text-xs h-7" data-testid={`sd-install-date-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-[10px]">Garanti (Yil)</Label>
                      <Input type="number" min="0" max="20" value={sd.warrantyYears} onChange={(e) => updateServiceDetail(idx, 'warrantyYears', e.target.value)}
                        placeholder="Or: 5" className="bg-gray-800 border-gray-700 text-white text-xs h-7"
                        data-testid={`sd-warranty-${idx}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-[10px]">Hizmet Notu</Label>
                    <Input value={sd.notes} onChange={(e) => updateServiceDetail(idx, 'notes', e.target.value)}
                      placeholder="Bu hizmete ozel not..." className="bg-gray-800 border-gray-700 text-white text-xs h-7" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ───── FIYAT & SURE + AI ───── */}
          <div className="border border-green-700/40 rounded-lg p-3 space-y-3 bg-green-900/10">
            <AIEstimationCard
              estimation={estimation} showEstDetail={showEstDetail} setShowEstDetail={setShowEstDetail}
              applyEstimation={applyEstimation} fetchEstimation={fetchEstimation}
              estLoading={estLoading} canEstimate={formData.selectedServiceIds.length > 0}
            />
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Toplam Fiyat (TL)</Label>
                <Input type="number" value={formData.totalPrice} onChange={f('totalPrice')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-price-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Odenen (TL)</Label>
                <Input type="number" value={formData.paidAmount} onChange={f('paidAmount')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-paid-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Bonus (%)</Label>
                <Input type="number" min="0" max="100" value={formData.bonusPercentage} onChange={f('bonusPercentage')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-bonus-input" />
              </div>
            </div>
          </div>

          {/* ───── TARIHLER & GARANTI ───── */}
          <div className="border border-cyan-700/40 rounded-lg p-3 space-y-3 bg-cyan-900/10">
            <span className="text-cyan-400 font-semibold font-display text-xs">Tarihler & Garanti</span>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Gelis Tarihi</Label>
                <Input type="date" value={formData.arrivalDate} onChange={f('arrivalDate')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-arrival-date" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Hedef Tamamlanma</Label>
                <Input type="date" value={formData.targetCompletionDate} onChange={f('targetCompletionDate')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-target-date" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Uygulama Tarihi</Label>
                <Input type="date" value={formData.installationDate} onChange={f('installationDate')}
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-install-date" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Garanti Suresi (Yil)</Label>
                <Input type="number" min="0" max="20" value={formData.warrantyYears} onChange={f('warrantyYears')} placeholder="Or: 5"
                  className="bg-gray-900 border-gray-700 text-white text-sm" data-testid="job-warranty-years" />
              </div>
            </div>
          </div>

          {/* ───── İŞ DURUMU (admin/yönetici düzenleme modunda) ───── */}
          {job && (() => {
            const currentUser = getFromLocalStorage('currentUser');
            const userRole = currentUser?.role || '';
            const canChangeStatus = ['admin', 'manager'].includes(userRole) || currentUser?.isAdmin;
            if (!canChangeStatus) return null;
            return (
              <div className="border border-amber-500/30 rounded-lg p-3 space-y-2 bg-amber-500/5">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Label className="font-display font-semibold text-amber-400 text-xs">
                      İş Durumu (ileri/geri alınabilir)
                    </Label>
                    <Select
                      value={formData.status || ''}
                      onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                    >
                      <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="job-status-select">
                        <SelectValue placeholder="Durum seç..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_MAP).map(([key, [label]]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-500">
                      Durum her iki yönde değiştirilebilir. Dikkatle kullanın.
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ───── PERSONEL ATAMA ───── */}
          <div className="border border-gray-700/40 rounded-lg p-3 space-y-3 bg-gray-900/30">
            <span className="text-gray-400 font-semibold font-display text-xs">Personel Atama</span>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-500 text-xs">Satis Danismani</Label>
                <select value={formData.salesId} onChange={(e) => handleStaffChange('salesId', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-xs" data-testid="job-sales-select">
                  <option value="">Sec...</option>
                  {branchFilteredSales.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role === 'sales' ? 'Satis' : s.role === 'manager' ? 'Yonetici' : 'Admin'})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Uygulamaci</Label>
                <select value={formData.technicianId} onChange={(e) => handleStaffChange('technicianId', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-xs" data-testid="job-tech-select">
                  <option value="">Sec...</option>
                  {branchFilteredTech.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role === 'technician' ? 'Uygulamaci' : s.role === 'manager' ? 'Yonetici' : 'Admin'})</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ───── NOTLAR ───── */}
          <div className="border border-gray-700/40 rounded-lg p-3 space-y-2 bg-gray-900/30">
            <span className="text-gray-400 font-semibold font-display text-xs">Notlar</span>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-500 text-xs">Genel Is Notu</Label>
                <textarea value={formData.notes} onChange={f('notes')} placeholder="Genel is notu..." rows={2}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-xs resize-none" data-testid="job-notes" />
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Musteri Notlari</Label>
                <textarea value={formData.customerNotes} onChange={f('customerNotes')} placeholder="Musterinin istekleri, dikkat edilecekler..." rows={2}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-xs resize-none" data-testid="job-customer-notes" />
              </div>
            </div>
          </div>

          {/* ───── SUBMIT ───── */}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700 text-sm">Iptal</Button>
            <Button type="submit" disabled={loading} data-testid="job-submit-btn"
              className="flex-1 bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90 text-white text-sm">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Kaydediliyor...</> : (job ? 'Guncelle' : 'Is Olustur')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    {/* ───── YENİ ARAÇ MODAL ───── */}
    <Dialog open={showNewVehicleModal} onOpenChange={setShowNewVehicleModal}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Yeni Araç Ekle</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-gray-400 text-xs">Plaka *</Label>
            <Input
              value={newVehicleForm.plate}
              onChange={(e) => setNewVehicleForm(p => ({ ...p, plate: e.target.value.toUpperCase() }))}
              placeholder="35 ABC 123"
              className="bg-gray-900 border-gray-700 text-white font-mono tracking-wider"
              data-testid="new-vehicle-plate"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-gray-400 text-xs">Marka</Label>
              <Input
                value={newVehicleForm.brand}
                onChange={(e) => setNewVehicleForm(p => ({ ...p, brand: e.target.value }))}
                placeholder="BMW"
                className="bg-gray-900 border-gray-700 text-white"
                data-testid="new-vehicle-brand"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-400 text-xs">Model</Label>
              <Input
                value={newVehicleForm.model}
                onChange={(e) => setNewVehicleForm(p => ({ ...p, model: e.target.value }))}
                placeholder="3 Serisi"
                className="bg-gray-900 border-gray-700 text-white"
                data-testid="new-vehicle-model"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowNewVehicleModal(false)}
            className="border-gray-600 text-gray-300 hover:bg-gray-700">
            Iptal
          </Button>
          <Button
            className="bg-brand hover:bg-brand/90 text-white"
            disabled={!newVehicleForm.plate.trim()}
            data-testid="save-new-vehicle-btn"
            onClick={async () => {
              try {
                const resp = await fetch(`${BACKEND_URL}/api/vehicles`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    customerId: formData.customerId,
                    plate: newVehicleForm.plate.trim(),
                    brand: newVehicleForm.brand.trim(),
                    model: newVehicleForm.model.trim(),
                  }),
                });
                if (!resp.ok) {
                  const err = await resp.json().catch(() => ({}));
                  toast({ title: "Hata", description: err.detail || "Araç eklenemedi", variant: "destructive" });
                  return;
                }
                const created = await resp.json();
                const newV = created?.vehicle || created;
                setCustomerVehicles(prev => [...prev, newV]);
                setFormData(prev => ({
                  ...prev,
                  vehicleId: newV.id,
                  carPlate: newV.plate,
                  carDetails: `${newV.brand || ''} ${newV.model || ''}`.trim(),
                }));
                setNewVehicleForm({ plate: '', brand: '', model: '' });
                setShowNewVehicleModal(false);
                toast({ title: "Araç eklendi", description: newV.plate });
              } catch (err) {
                toast({ title: "Hata", description: err.message, variant: "destructive" });
              }
            }}
          >
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default AddEditJob;
